import { CoreErrors } from '../errors.js';
const SEMVER_PATTERN = /^(\d+)\.(\d+)\.(\d+)(?:-([0-9A-Za-z.-]+))?$/;
export function parseSemver(version) {
    const match = SEMVER_PATTERN.exec(version.trim());
    if (match === null)
        throw CoreErrors.invalidSemver(version);
    const parsed = {
        major: Number(match[1]),
        minor: Number(match[2]),
        patch: Number(match[3]),
    };
    if (match[4] !== undefined)
        parsed.prerelease = match[4];
    return parsed;
}
export function formatSemver(version) {
    const core = `${version.major}.${version.minor}.${version.patch}`;
    return version.prerelease === undefined ? core : `${core}-${version.prerelease}`;
}
function comparePrerelease(a, b) {
    if (a === b)
        return 0;
    // Une version sans pré-version est postérieure à la même version en pré-version (SemVer §11.3).
    if (a === undefined)
        return 1;
    if (b === undefined)
        return -1;
    const left = a.split('.');
    const right = b.split('.');
    for (let index = 0; index < Math.max(left.length, right.length); index += 1) {
        const l = left[index];
        const r = right[index];
        if (l === undefined)
            return -1;
        if (r === undefined)
            return 1;
        const lNumeric = /^\d+$/.test(l);
        const rNumeric = /^\d+$/.test(r);
        if (lNumeric && rNumeric) {
            const diff = Number(l) - Number(r);
            if (diff !== 0)
                return diff < 0 ? -1 : 1;
            continue;
        }
        if (lNumeric !== rNumeric)
            return lNumeric ? -1 : 1;
        if (l !== r)
            return l < r ? -1 : 1;
    }
    return 0;
}
export function compareSemver(a, b) {
    const left = typeof a === 'string' ? parseSemver(a) : a;
    const right = typeof b === 'string' ? parseSemver(b) : b;
    for (const key of ['major', 'minor', 'patch']) {
        if (left[key] !== right[key])
            return left[key] < right[key] ? -1 : 1;
    }
    return comparePrerelease(left.prerelease, right.prerelease);
}
/** Nature de l'écart entre deux versions : ce que le bump dit du changement (15 §56). */
export function bumpBetween(previous, next) {
    const from = typeof previous === 'string' ? parseSemver(previous) : previous;
    const to = typeof next === 'string' ? parseSemver(next) : next;
    if (to.major !== from.major)
        return 'major';
    if (to.minor !== from.minor)
        return 'minor';
    if (to.patch !== from.patch)
        return 'patch';
    return 'none';
}
const BUMP_RANK = { none: 0, patch: 1, minor: 2, major: 3 };
export function isAtLeastBump(actual, required) {
    return BUMP_RANK[actual] >= BUMP_RANK[required];
}
function comparatorsFor(part) {
    const token = part.trim();
    if (token === '' || token === '*' || token === 'x')
        return [];
    const caretOrTilde = /^([\^~])(.+)$/.exec(token);
    if (caretOrTilde !== null) {
        const base = parseSemver(caretOrTilde[2]);
        const upper = caretOrTilde[1] === '~' || base.major === 0
            ? // ~1.2.3 et ^0.2.3 : la borne haute est la minor suivante.
                { major: base.major, minor: base.minor + 1, patch: 0 }
            : { major: base.major + 1, minor: 0, patch: 0 };
        return [
            { operator: '>=', version: base },
            { operator: '<', version: upper },
        ];
    }
    const wildcard = /^(\d+)\.(?:x|\*)$|^(\d+)\.(\d+)\.(?:x|\*)$/.exec(token);
    if (wildcard !== null) {
        if (wildcard[1] !== undefined) {
            const major = Number(wildcard[1]);
            return [
                { operator: '>=', version: { major, minor: 0, patch: 0 } },
                { operator: '<', version: { major: major + 1, minor: 0, patch: 0 } },
            ];
        }
        const major = Number(wildcard[2]);
        const minor = Number(wildcard[3]);
        return [
            { operator: '>=', version: { major, minor, patch: 0 } },
            { operator: '<', version: { major, minor: minor + 1, patch: 0 } },
        ];
    }
    const explicit = /^(>=|<=|>|<|=)?\s*(\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?)$/.exec(token);
    if (explicit === null)
        throw CoreErrors.invalidRange(token);
    return [{ operator: explicit[1] ?? '=', version: parseSemver(explicit[2]) }];
}
function satisfiesComparator(version, comparator) {
    const order = compareSemver(version, comparator.version);
    switch (comparator.operator) {
        case '=':
            return order === 0;
        case '>':
            return order > 0;
        case '>=':
            return order >= 0;
        case '<':
            return order < 0;
        case '<=':
            return order <= 0;
    }
}
/**
 * Une version satisfait-elle une plage ?
 *
 * Une **pré-version** ne satisfait qu'une plage qui la nomme exactement : `1.0.0-rc.1` ne passe
 * pas sous `^1.0.0`, sans quoi une publication de production hériterait d'une release candidate.
 */
export function satisfiesRange(version, range) {
    const candidate = typeof version === 'string' ? parseSemver(version) : version;
    const unions = range.split('||');
    return unions.some((union) => {
        const comparators = union.trim().split(/\s+/).flatMap(comparatorsFor);
        if (comparators.length === 0)
            return candidate.prerelease === undefined;
        if (candidate.prerelease !== undefined) {
            return comparators.every((comparator) => comparator.operator === '=' && compareSemver(candidate, comparator.version) === 0);
        }
        return comparators.every((comparator) => satisfiesComparator(candidate, comparator));
    });
}
//# sourceMappingURL=semver.js.map